import React, {useState} from 'react'
import {Button} from "primereact/button";
import PlayerUtil from "../../../../util/PlayerUtil";
import SpielDetailsDialog from "./SpielDetailsDialog";
import SchweizerSortierungDialog from "./SchweizerSortierungDialog";
import {useSchmaleAnsicht} from "./statistikWerte";

/**
 * Runden im Schweizer System für die neue öffentliche Ansicht (nur lesen): je Runde die Tabelle und die Spiele,
 * die neueste Runde zuerst. Fährt man über einen Spieler, wird er in Tabelle und Spielen der Runde hervorgehoben.
 */
export default function SchweizerRundenBereich({tournament, gruppenTabellen, boardGames, liveGames}) {

    const [gruppe, setGruppe] = useState(1);
    const [platzierungenAktiv, setPlatzierungenAktiv] = useState(false);
    const [markierterSpieler, setMarkierterSpieler] = useState(undefined);
    const [showSpiele, setShowSpiele] = useState(false);
    const [showSortierungDialog, setShowSortierungDialog] = useState(false);
    const [statistikGameId, setStatistikGameId] = useState(undefined);
    // Handy: Tabelle als kompakte Liste
    const schmal = useSchmaleAnsicht();

    const modusGruppenphase = tournament.tournamentModusEntities.find(element => element.modusTyp === "gruppenturnier");
    const isSetModus = modusGruppenphase !== undefined && modusGruppenphase.sets > 1;
    const rundenphaseBeendet = vorhanden(tournament.goldTurnierId);
    const showBuchholz = tournament.buchholzWertung === true;
    // bei Buchholz-Wertung sind die Spiele-Spalten optional und standardmäßig ausgeblendet
    const spieleSichtbar = !showBuchholz || showSpiele;
    const spielerName = (playerId) => PlayerUtil.getSpielerName(tournament.tournamentPlayerEntities, playerId);

    const groupGames = tournament.tournamentGroupGameEntities.filter(groupGame => groupGame.gruppe === gruppe);
    const anzahlRunden = groupGames.reduce((max, groupGame) => Math.max(max, groupGame.runde), 1);
    const runden = [];
    for (let runde = anzahlRunden; runde > 0; runde--) {
        runden.push(runde);
    }

    const getGruppenAuswahl = () => {
        if (tournament.anzahlGruppen <= 1) {
            return null;
        }
        const eintraege = [];
        for (let i = 1; i <= tournament.anzahlGruppen; i++) {
            eintraege.push({id: "gruppe" + i, label: "Gruppe " + i, aktiv: !platzierungenAktiv && gruppe === i, waehle: () => {
                setGruppe(i);
                setPlatzierungenAktiv(false);
            }});
        }
        if (tournament.platzierungsphase) {
            eintraege.push({id: "platzierungen", label: "Platzierungen", aktiv: platzierungenAktiv, waehle: () => setPlatzierungenAktiv(true)});
        }
        return <div className="dhpt-segment" role="radiogroup" aria-label="Gruppe">
            {eintraege.map(e => <button key={e.id} type="button" role="radio" aria-checked={e.aktiv} className="dhpt-segment-knopf" onClick={e.waehle}>
                {e.label}
            </button>)}
        </div>
    }

    // Handy: je Spieler eine Zeile mit Platz, Name und Punkten, die übrigen Werte klein darunter
    const getTabelleSchmal = (tabelle, anzahlWeiter) => {
        return <ol className="dhpt-sr-liste" aria-label="Tabelle">
            {tabelle.rangliste.map((eintrag, index) => {
                const klassen = ["dhpt-sr-listen-zeile"];
                if (index < anzahlWeiter) {
                    klassen.push("dhpt-sr-weiter");
                }
                if (markierterSpieler !== undefined && markierterSpieler === eintrag.playerid) {
                    klassen.push("dhpt-sr-markiert");
                }
                const werte = [];
                if (showBuchholz) {
                    werte.push("BH " + eintrag.buchholz, "BHF " + eintrag.feinBuchholz);
                }
                if (spieleSichtbar) {
                    werte.push("S/U/V " + eintrag.gewonnenespiele + "/" + eintrag.unentschiedenspiele + "/" + eintrag.verlorenespiele);
                }
                werte.push((isSetModus ? "Sets " : "Legs ") + eintrag.anzahlLegsGewonnen + ":" + eintrag.anzahlLegsVerloren);
                return <li key={eintrag.playerid} className={klassen.join(" ")}
                           onClick={() => setMarkierterSpieler(markierterSpieler === eintrag.playerid ? undefined : eintrag.playerid)}>
                    <span className="dhpt-sr-listen-pos">{index + 1}</span>
                    <span className="dhpt-sr-listen-mitte">
                        <span className="dhpt-sr-listen-name">{spielerName(eintrag.playerid)}</span>
                        <span className="dhpt-sr-listen-werte">{werte.join(" · ")}</span>
                    </span>
                    <span className="dhpt-sr-listen-punkte">{eintrag.punkte}<span className="dhpt-sr-listen-punkte-label">Pkt</span></span>
                </li>
            })}
        </ol>
    }

    const getTabelle = (runde) => {
        const tabelle = gruppenTabellen.find(t => t.gruppe === gruppe && t.runde === runde);
        if (!tabelle) {
            return <p className="dhpt-hinweis">Noch keine Tabelle vorhanden.</p>;
        }
        const anzahlWeiter = tournament.anzahlSpielerGold;
        if (schmal) {
            return getTabelleSchmal(tabelle, anzahlWeiter);
        }
        return <div className="dhpt-sr-tabelle-rahmen">
            <table className="dhpt-sr-tabelle">
                <thead>
                <tr>
                    <th className="dhpt-sr-pos">#</th>
                    <th className="dhpt-sr-name-spalte">Spieler</th>
                    <th>Pkt</th>
                    {showBuchholz && <><th title="Buchholz">BH</th><th title="Feinbuchholz">BHF</th></>}
                    {spieleSichtbar && <><th title="Siege">S</th><th title="Unentschieden">U</th><th title="Niederlagen">V</th></>}
                    <th title={isSetModus ? "Sets gewonnen : verloren" : "Legs gewonnen : verloren"}>{isSetModus ? "Sets" : "Legs"}</th>
                </tr>
                </thead>
                <tbody>
                {tabelle.rangliste.map((eintrag, index) => {
                    const klassen = ["dhpt-sr-zeile"];
                    if (index < anzahlWeiter) {
                        klassen.push("dhpt-sr-weiter");
                    }
                    if (markierterSpieler !== undefined && markierterSpieler === eintrag.playerid) {
                        klassen.push("dhpt-sr-markiert");
                    }
                    return <tr key={eintrag.playerid} className={klassen.join(" ")}
                               onMouseEnter={() => setMarkierterSpieler(eintrag.playerid)} onMouseLeave={() => setMarkierterSpieler(undefined)}>
                        <td className="dhpt-sr-pos">{index + 1}</td>
                        <td className="dhpt-sr-name-spalte">{spielerName(eintrag.playerid)}</td>
                        <td className="dhpt-sr-punkte">{eintrag.punkte}</td>
                        {showBuchholz && <><td>{eintrag.buchholz}</td><td>{eintrag.feinBuchholz}</td></>}
                        {spieleSichtbar && <><td>{eintrag.gewonnenespiele}</td><td>{eintrag.unentschiedenspiele}</td><td>{eintrag.verlorenespiele}</td></>}
                        <td className="dhpt-sr-legs">{eintrag.anzahlLegsGewonnen}:{eintrag.anzahlLegsVerloren}</td>
                    </tr>
                })}
                </tbody>
            </table>
        </div>
    }

    const getSpiele = (runde) => {
        const spiele = groupGames.filter(groupGame => groupGame.runde === runde);
        if (spiele.length === 0) {
            return <p className="dhpt-hinweis">Keine Spiele in dieser Runde.</p>;
        }
        return <div className="dhpt-sr-spiele">
            {spiele.map(game => <Spiel key={game.id} spiel={rundenSpiel(game)} statistikNurMitScoring={true} tournament={tournament} spielerName={spielerName}
                                       boardGames={boardGames} liveGames={liveGames} markierterSpieler={markierterSpieler}
                                       onMarkiere={setMarkierterSpieler} onStatistik={setStatistikGameId}/>)}
        </div>
    }

    // Platzierungsspiele nach dem Platz, um den gespielt wird
    const getPlatzierungsspiele = () => {
        const spiele = [...(tournament.tournamentSchweizerPlatzierungGameEntities || [])].sort((a, b) => a.spielUmPlatz - b.spielUmPlatz);
        if (spiele.length === 0) {
            return <p className="dhpt-hinweis">Aktuell keine Spiele vorhanden.</p>;
        }
        return <div className="dhpt-sr-spiele dhpt-sr-platzierungen">
            {spiele.map(game => <Spiel key={game.id} spiel={platzierungsSpiel(game)} statistikNurMitScoring={false} tournament={tournament}
                                       spielerName={spielerName} boardGames={boardGames} liveGames={liveGames} markierterSpieler={markierterSpieler}
                                       onMarkiere={setMarkierterSpieler} onStatistik={setStatistikGameId}/>)}
        </div>
    }

    return <div className="dhpt-inhalt-eigen dhpt-sr">
        <div className="dhpt-sr-leiste">
            {getGruppenAuswahl()}
            {rundenphaseBeendet && <span className="dhpt-chip dhpt-chip-live">Rundenphase beendet</span>}
            <span className="dhpt-sr-leiste-rechts">
                {showBuchholz && !platzierungenAktiv &&
                    <Button icon={showSpiele ? "pi pi-eye-slash" : "pi pi-eye"} label={showSpiele ? "Spiele-Spalten aus" : "Spiele-Spalten ein"}
                            className="p-button-sm p-button-outlined dhpt-button-hell" onClick={() => setShowSpiele(!showSpiele)}/>
                }
                <Button icon="pi pi-sort-amount-down" label="Sortierung" className="p-button-sm p-button-outlined dhpt-button-hell"
                        onClick={() => setShowSortierungDialog(true)}/>
            </span>
        </div>

        {platzierungenAktiv
            ? <section className="dhpt-karte">
                <h3 className="dhpt-karte-titel"><span>Platzierungsspiele</span></h3>
                {getPlatzierungsspiele()}
            </section>
            : runden.map(runde => <section key={runde} className="dhpt-karte dhpt-sr-runde">
                <h3 className="dhpt-karte-titel">
                    <span>Runde {runde}</span>
                    {tournament.anzahlGruppen > 1 && <span className="dhpt-karte-zusatz">Gruppe {gruppe}</span>}
                </h3>
                <div className="dhpt-sr-runde-inhalt">
                    <div className="dhpt-sr-teil">
                        <h4 className="dhpt-sr-teil-titel">Tabelle</h4>
                        {getTabelle(runde)}
                    </div>
                    <div className="dhpt-sr-teil">
                        <h4 className="dhpt-sr-teil-titel">Spiele</h4>
                        {getSpiele(runde)}
                    </div>
                </div>
            </section>)
        }

        {tournament.anzahlSpielerGold > 0 && !platzierungenAktiv &&
            <p className="dhpt-hinweis"><span className="dhpt-sr-legende"/>Die ersten {tournament.anzahlSpielerGold} kommen weiter.</p>
        }

        {showSortierungDialog &&
            <SchweizerSortierungDialog tournamentId={tournament.id} players={tournament.tournamentPlayerEntities}
                                       playerId={getMarkierterSpielerId(tournament.tournamentPlayerEntities, markierterSpieler)}
                                       onClose={() => setShowSortierungDialog(false)}/>
        }
        {statistikGameId !== undefined &&
            <SpielDetailsDialog tournamentId={tournament.id} tournamentGameId={statistikGameId.id} ergebnisVomScorer={statistikGameId.ergebnisVomScorer === true}
                                 onClose={() => setStatistikGameId(undefined)}/>
        }
    </div>
}

// Rundenspiel bzw. Platzierungsspiel auf eine gemeinsame Form bringen (auch für die Gruppen im Gruppenturnier)
export function rundenSpiel(game) {
    return {
        id: game.id, spieler1: game.spieler1, spieler2: game.spieler2, anzahl1: game.anzahlSpieler1, anzahl2: game.anzahlSpieler2,
        ohneGegner: game.spielOhneErgebnis === true, schreiberName: game.schreiberName, schreiberId: game.schreiberId,
        ergebnisVomScorer: game.ergebnisVomScorer
    };
}

function platzierungsSpiel(game) {
    return {
        id: game.id, spieler1: game.player1Id, spieler2: game.player2Id, anzahl1: game.anzahlPlayer1, anzahl2: game.anzahlPlayer2,
        ohneGegner: !vorhanden(game.player2Id), ergebnisVomScorer: game.ergebnisVomScorer, titel: "Spiel um Platz " + game.spielUmPlatz
    };
}

export function Spiel({spiel, statistikNurMitScoring, tournament, spielerName, boardGames, liveGames, markierterSpieler, onMarkiere, onStatistik}) {
    const game = spiel;
    const ohneGegner = game.ohneGegner;
    const ergebnisVorhanden = vorhanden(game.anzahl1) && vorhanden(game.anzahl2);
    const sieger1 = ergebnisVorhanden && Number(game.anzahl1) > Number(game.anzahl2);
    const sieger2 = ergebnisVorhanden && Number(game.anzahl2) > Number(game.anzahl1);
    const boardGame = (boardGames || []).find(boardGame => boardGame.tournamentGameId === game.id);
    const liveGame = boardGame ? (liveGames || []).find(liveGame => liveGame.gameId === boardGame.gameId) : undefined;
    // Rundenspiele haben nur mit Darthelfer-Scoring eine Statistik, bei Platzierungsspielen wie bisher immer
    const statistikMoeglich = ergebnisVorhanden && !ohneGegner && (!statistikNurMitScoring || tournament.scoringWithDarthelfer);

    const spieler = (playerId, name, sieger) => <span
        className={"dhpt-sr-spieler" + (sieger ? " dhpt-sr-sieger" : "") + (markierterSpieler !== undefined && markierterSpieler === playerId ? " dhpt-sr-markiert" : "")}
        onMouseEnter={() => onMarkiere(playerId)} onMouseLeave={() => onMarkiere(undefined)}>{name}</span>;

    const getStand = () => {
        if (ergebnisVorhanden) {
            return game.anzahl1 + ":" + game.anzahl2;
        }
        if (liveGame) {
            if (liveGame.startssoon || liveGame.inGame) {
                return "–";
            }
            return liveGame.sets > 1 ? liveGame.spieler1Sets + ":" + liveGame.spieler2Sets : liveGame.spieler1Legs + ":" + liveGame.spieler2Legs;
        }
        return "–";
    }

    const stand = <span className={"dhpt-sr-stand" + (!ergebnisVorhanden && liveGame ? " dhpt-sr-stand-live" : "")}>{getStand()}</span>;

    return <div className="dhpt-sr-spiel">
        {game.titel && <div className="dhpt-sr-spiel-titel">{game.titel}</div>}
        <div className="dhpt-sr-spiel-zeile">
            {spieler(game.spieler1, spielerName(game.spieler1), sieger1)}
            {statistikMoeglich
                ? <button type="button" className="dhpt-sr-stand-knopf" title="Statistik zum Spiel" onClick={() => onStatistik(game)}>{stand}</button>
                : stand}
            {ohneGegner ? <span className="dhpt-sr-spieler dhpt-sr-freilos">Freilos</span> : spieler(game.spieler2, spielerName(game.spieler2), sieger2)}
        </div>
        {(boardGame || vorhanden(game.schreiberName)) &&
            <div className="dhpt-sr-spiel-info">
                {boardGame && <span className={"dhpt-chip" + (liveGame && !liveGame.startssoon && !liveGame.inGame ? " dhpt-chip-live" : "")}>
                    {liveGame && liveGame.startssoon ? "Startet · " : liveGame && liveGame.inGame ? "Im Spiel · " : ""}{boardGame.boardBezeichnung}
                </span>}
                {boardGame && vorhanden(boardGame.schreiberName) && <span>Schreiber: {boardGame.schreiberName}</span>}
                {!boardGame && vorhanden(game.schreiberName) &&
                    <span className={markierterSpieler !== undefined && markierterSpieler === game.schreiberId ? "dhpt-sr-markiert" : undefined}>Schreiber: {game.schreiberName}</span>}
            </div>
        }
    </div>
}

function vorhanden(wert) {
    return wert !== null && wert !== undefined && wert !== "";
}

// markierte Spieler können über id oder userId kommen (wie bei PlayerUtil.getSpielerName), das Protokoll braucht die id
function getMarkierterSpielerId(players, markierterSpieler) {
    if (markierterSpieler === undefined || !players) {
        return undefined;
    }
    const spieler = players.find(p => p.id === markierterSpieler || p.userId === markierterSpieler);
    return spieler ? spieler.id : undefined;
}
